import {LitElement, html, css} from 'lit';
import '../components/clam-spinner.js';

interface ConfirmData {
	personaName: string;
	eventName: string;
	alreadyConfirmed: boolean;
}

export class ClamConfirmar extends LitElement {
	static styles = css`
		:host {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 100vh;
			font-family: 'Helvetica', Arial, sans-serif;
			background: linear-gradient(135deg, #5a9d1e 0%, #3d7a14 100%);
			padding: 16px;
			box-sizing: border-box;
		}
		.card {
			background: #fff;
			border-radius: 14px;
			padding: 28px 20px;
			max-width: 440px;
			width: 100%;
			text-align: center;
			box-shadow: 0 16px 48px rgba(0,0,0,0.28);
			box-sizing: border-box;
		}
		@media (min-width: 480px) { .card { padding: 40px 32px; border-radius: 16px; } }
		.logo {
			max-width: 150px;
			width: 54%;
			height: auto;
			margin: 0 auto 18px;
			display: block;
		}
		@media (min-width: 480px) { .logo { max-width: 180px; } }
		.icon-circle {
			width: 72px;
			height: 72px;
			border-radius: 50%;
			margin: 0 auto 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 36px;
		}
		@media (min-width: 480px) { .icon-circle { width: 80px; height: 80px; font-size: 40px; } }
		.icon-success { background: #dcfce7; }
		.icon-loading { background: #e8f4fd; }
		.icon-error { background: #fee2e2; }
		h1 {
			color: #1a3a5c;
			font-size: clamp(19px, 5.2vw, 26px);
			line-height: 1.25;
			margin: 0 0 8px;
		}
		.event-name {
			color: #6d6e71;
			font-size: 14px;
			margin: 0 0 20px;
		}
		@media (min-width: 480px) { .event-name { font-size: 15px; margin-bottom: 24px; } }
		.persona-name {
			color: #1a3a5c;
			font-size: 18px;
			font-weight: 700;
			margin: 0 0 12px;
		}
		@media (min-width: 480px) { .persona-name { font-size: 20px; } }
		.message {
			color: #555;
			font-size: 14px;
			line-height: 1.6;
			margin: 0 0 20px;
		}
		@media (min-width: 480px) { .message { font-size: 15px; margin-bottom: 24px; } }
		.already {
			background: #fef3c7;
			color: #92400e;
			padding: 10px 14px;
			border-radius: 8px;
			font-size: 13px;
			margin-bottom: 18px;
		}
		.footer {
			margin-top: 26px;
			padding-top: 20px;
			border-top: 1px solid #eef0f4;
			color: #999;
			font-size: 12px;
		}
		.error-msg {
			color: #c33;
			font-size: 14px;
			margin: 0 0 14px;
		}
	`;

	private _state: 'loading' | 'success' | 'already' | 'error' = 'loading';
	private _data: ConfirmData | null = null;
	private _errorMsg = '';
	private _token = '';

	static get properties() {
		return {token: {type: String}};
	}

	set token(val: string) {
		this._token = val;
		if (val) void this._confirm();
	}

	connectedCallback(): void {
		super.connectedCallback();
	}

	private async _confirm(): Promise<void> {
		try {
			const res = await fetch(`/api/public/confirmar/${this._token}`);
			const json = await res.json();
			if (!res.ok) {
				this._state = 'error';
				this._errorMsg = json.error || 'No se pudo confirmar.';
			} else {
				this._data = json as ConfirmData;
				this._state = json.alreadyConfirmed ? 'already' : 'success';
			}
		} catch (e) {
			this._state = 'error';
			this._errorMsg = e instanceof Error ? e.message : 'Error de conexión.';
		}
		this.requestUpdate();
	}

	render() {
		const logo = html`<img class="logo" src="/img/branding/logo_navbar.png" alt="CLAM Veracruz" />`;
		if (this._state === 'loading') {
			return html`<div class="card">${logo}<div class="icon-circle icon-loading"><clam-spinner size="lg" color="#1a3a5c" track="#e0e5ec"></clam-spinner></div><h1>Confirmando...</h1></div>`;
		}
		if (this._state === 'error') {
			return html`<div class="card">${logo}<div class="icon-circle icon-error">✕</div><h1>No se pudo confirmar</h1><p class="error-msg">${this._errorMsg}</p><p class="message">Si crees que es un error, contacta al organizador del evento.</p></div>`;
		}
		return html`
			<div class="card">
				${logo}
				<div class="icon-circle icon-success">✓</div>
				<h1>¡Gracias por confirmar la recepción de tu acceso!</h1>
				<p class="persona-name">${this._data?.personaName}</p>
				<p class="event-name">${this._data?.eventName}</p>
				${this._state === 'already'
					? html`<div class="already">Ya habías confirmado la recepción de este correo.</div>`
					: ''}
				<p class="message">Hemos registrado que recibiste el correo con tu acceso. Te esperamos en el evento.<br/>Presenta tu código QR al llegar para tu check-in.</p>
				<p class="message" style="font-size:13px;color:#999;">Ya puedes cerrar esta página.</p>
				<div class="footer">CLAM Veracruz · Comité Local de Ayuda Mutua</div>
			</div>
		`;
	}
}

customElements.define('clam-confirmar', ClamConfirmar);
